import AppConfig from 'constants/AppConfig';
import generator from 'generate-password';
import moment from 'moment-timezone';
import { useState } from 'react';
import Geocode from 'react-geocode';
import { NotificationManager } from 'react-notifications';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import IntlMessages from 'utils/intl-messages';

Geocode.setApiKey('AIzaSyD0eC_j5MVeV5YsOIRnLVRyIx9npurleUA');

interface FormValue {
  [key: string]: any;
}

interface FileValue {
  [key: string]: any;
}

interface FormResult {
  value: FormValue;
  file: FileValue;
  setValue: (value: FormValue) => void;
  setFile: (file: FileValue) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeNumber: (value: any, name: string) => void;
  onCheckChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMapClick: (e: any) => void;
  onChangeGoogleAddress: (address: string) => void;
  onSelectGoogleAddress: (address: string) => void;
  generatePassword: () => void;
  toggleValue: (passedValue: string) => void;
  editFile: (inputName: string, resizedName: string, fileId: string) => void;
  onUploadFile: (data: any, inputName: string, resizedName: string, fileId: string) => void;
  clearFields: (initFields: FormValue) => void;
  onChangeEditor: (html: string, name: string) => void;
  onEdit: (e: any, id: string, props: any, customPath?: string) => void;
  onChangeTab: (e: any, newValue: any) => void;
  onChangeFilterDate: (date: any, name: string) => void;
  changeAutoComplete: (e: any, val: any, name: string) => void;
  onChangeDate: (date: any, name: string) => void;
  blockInvalidChar: (e: React.KeyboardEvent, array: string[]) => void;
  onChangeTime: (date: any, name: string) => void;
  changeAutoCompleteTextField: (e: React.ChangeEvent<HTMLInputElement>, name: string) => void;
}

export default (input: FormValue, filesInput: FileValue = {} as FileValue): FormResult => {
  const [value, setInfo] = useState<FormValue>(input);
  const [file, setFile] = useState<FileValue>(filesInput);

  const setValue = (value: FormValue): void => {
    setInfo(value);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e) {
      setInfo({
        ...value,
        [e.target.name]: e.target.value,
      });
    }
  };

  const onChangeNumber = (value: any, name: string): void => {
    setInfo({
      ...value,
      [name]: value,
    });
  };

  const onCheckChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e) {
      setInfo({
        ...value,
        [e.target.name]: e.target.checked,
      });
    }
  };

  const clearFields = (initFields: FormValue): void => {
    setInfo({
      ...value,
      ...initFields,
    });
  };

  const onMapClick = (e: any): void => {
    let NewAddress: string;
    Geocode.fromLatLng(e.latLng.lat(), e.latLng.lng()).then(
      (response: any) => {
        NewAddress = response.results[0].formatted_address;
        setInfo({
          ...value,
          fullLine: NewAddress,
          coordinates: {
            type: 'Point',
            coordinates: [e.latLng.lng(), e.latLng.lat()],
          },
          lat: e.latLng.lat(),
          long: e.latLng.lng(),
        });
      },
      (error: any) => {
        console.error(error);
      }
    );
  };

  //places actions
  const onChangeGoogleAddress = (address: string): void =>
    setInfo({
      ...value,
      fullLine: address || ' ',
    });

  const onSelectGoogleAddress = (address: string): void => {
    geocodeByAddress(address)
      .then((results: any[]) => {
        getLatLng(results[0])
          .then(({ lat, lng }: { lat: number; lng: number }) => {
            setInfo({
              ...value,
              fullLine: results[0].formatted_address,
              coordinates: {
                type: 'Point',
                coordinates: [lng, lat],
              },
              lat,
              long: lng,
            });
          })
          .catch((error: any) => console.error('Error getLatLng', error));
      })
      .catch((error: any) => console.error('Error geocodeByAddress', error));
  };

  //password actions
  const generatePassword = (): void => {
    const password = generator.generate({
      length: 8,
      numbers: true,
      uppercase: true,
      lowercase: true,
      exclude: '',
    });
    setInfo({ ...value, password });
  };

  const toggleValue = (passedValue: string): void => {
    setInfo({
      ...value,
      [passedValue]: !value[passedValue],
    });
  };

  //File actions
  const onUploadFile = (data: any, inputName: string, resizedName: string, fileId: string): void => {
    if (data) {
      setFile({
        ...file,
        [inputName]: data.url,
        [resizedName]: data.urlResized,
        [fileId]: data.fileId,
      });
    } else
      NotificationManager.error(
        <IntlMessages id={'global.errors.notUpload'} />,
        <IntlMessages id={`${AppConfig.errorTitle}`} />,
        AppConfig.notificationTimeout
      );
  };

  const editFile = (inputName: string, resizedName: string, fileId: string): void => {
    setFile({
      ...file,
      [inputName]: undefined,
      [resizedName]: undefined,
      [fileId]: undefined,
    });
  };

  const onChangeEditor = (html: string, name: string): void => {
    setInfo({
      ...value,
      [name]: html,
    });
  };

  const onChangeTab = (e: any, newValue: any): void => {
    if (e) {
      setInfo({
        ...value,
        activeTab: newValue,
      });
    }
  };

  const onEdit = (_e: any, id: string, props: any, customPath?: string): void => {
    props.history.push(`${customPath ? customPath : 'edit'}/${id}`);
  };

  const onChangeFilterDate = (date: any, name: string): void => {
    setInfo({
      ...value,
      [name]: {
        gte: moment(date)
          .utc(date.valueOf() + date.utcOffset() * 60000)
          .startOf('day')
          .toISOString(),
        lte: moment(date)
          .utc(date.valueOf() + date.utcOffset() * 60000)
          .endOf('day')
          .toISOString(),
      },
    });
  };

  const onChangeDate = (date: any, name: string): void => {
    setInfo({
      ...value,
      [name]: moment(date)
        .utc(date.valueOf() + date.utcOffset() * 60000)
        .startOf('day')
        .toISOString(),
    });
  };

  const onChangeTime = (date: any, name: string): void => {
    setInfo({ ...value, [name]: moment(date).toISOString() });
  };

  const changeAutoComplete = (_e: any, val: any, name: string): void => {
    setInfo({
      ...value,
      [name]: val,
    });
  };

  const changeAutoCompleteTextField = (e: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    setInfo({
      ...value,
      [name]: e.target.value,
    });
  };

  const blockInvalidChar = (e: React.KeyboardEvent, array: string[]): void => {
    array.includes(e.key) && e.preventDefault();
  };

  return {
    value,
    file,
    setValue,
    setFile,
    onChange,
    onChangeNumber,
    onCheckChange,
    onMapClick,
    onChangeGoogleAddress,
    onSelectGoogleAddress,
    generatePassword,
    toggleValue,
    editFile,
    onUploadFile,
    clearFields,
    onChangeEditor,
    onEdit,
    onChangeTab,
    onChangeFilterDate,
    changeAutoComplete,
    onChangeDate,
    blockInvalidChar,
    onChangeTime,
    changeAutoCompleteTextField,
  };
};
